import React, { useEffect, useState, useMemo } from "react";
import { api, API } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Loader2, Save, Plus, Minus, FileText, IndianRupee, TrendingUp, Building2, Sparkles, AlertTriangle, Lock, Boxes, Pencil, PlusCircle, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useSiteContext } from "../components/layout/Layout";

const fmt = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

const CAT_LABEL = {
  base_license: "Base SaaS License",
  hardware: "Hardware",
  cloud_license: "Cloud Licenses",
  accessory: "Accessories",
  lift_monitoring: "Lift Monitoring",
};
const CAT_ORDER = ["base_license", "hardware", "cloud_license", "lift_monitoring", "accessory"];

export default function SubscriptionRevenue() {
  const { sites, selectedSite } = useSiteContext();
  const [tab, setTab] = useState("configurator");
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);  // [{product_code, quantity, discount_pct}]
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState({});  // {code: {hw, cl}}
  const [overrideOpen, setOverrideOpen] = useState({});  // {code: bool}
  const token = localStorage.getItem("f360_token");

  // Load catalog once
  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)); }, []);

  // Load site config when site changes
  useEffect(() => {
    if (!selectedSite || selectedSite === "all") return;
    api.get(`/site-config/${selectedSite}`).then((r) => {
      setRows((r.data.rows || []).map((x) => ({
        product_code: x.product_code,
        quantity: x.quantity,
        discount_pct: x.discount_pct || 0,
        override_hardware_rate: x.override_hardware_rate ?? null,
        override_cloud_rate: x.override_cloud_rate ?? null,
      })));
    });
  }, [selectedSite]);

  const reloadCatalog = () => api.get("/catalog").then((r) => setCatalog(r.data));

  // Save master rate edit (debounced via blur)
  const saveMasterRate = async (code, field, value) => {
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) { toast.error("Invalid rate"); return; }
    try {
      await api.patch(`/catalog/${code}`, { [field]: num });
      toast.success("Master rate updated");
      await reloadCatalog();
      setEditing((e) => ({ ...e, [code]: { ...(e[code] || {}), [field]: undefined } }));
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  // Per-row override
  const setOverride = (code, field, value) => {
    setRows((prev) => {
      const found = prev.find((r) => r.product_code === code);
      const v = value === "" || value === null || value === undefined ? null : Number(value);
      if (!found) return [...prev, { product_code: code, quantity: 0, discount_pct: 0, [field]: v }];
      return prev.map((r) => r.product_code === code ? { ...r, [field]: v } : r);
    });
  };

  const addCustomProduct = async (form) => {
    try {
      const r = await api.post(`/catalog`, form);
      toast.success(`Added ${r.data.code} — ${r.data.name}`);
      setAddOpen(false);
      await reloadCatalog();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add product"); }
  };

  const deleteCustomProduct = async (code) => {
    if (!confirm(`Disable product ${code}? It will be hidden from new configs.`)) return;
    try { await api.delete(`/catalog/${code}`); toast.success("Product disabled"); await reloadCatalog(); }
    catch (err) { toast.error(err.response?.data?.detail || "Cannot disable (in use?)"); }
  };

  // Live preview whenever rows change
  useEffect(() => {
    if (!selectedSite || selectedSite === "all") return;
    const t = setTimeout(() => {
      api.post(`/site-config/${selectedSite}/preview`, { rows }).then((r) => setPreview(r.data));
      api.post(`/site-config/${selectedSite}/suggest`, { rows }).then((r) => setSuggestions(r.data.suggestions || []));
    }, 250);
    return () => clearTimeout(t);
  }, [rows, selectedSite]);

  // Tab loaders
  useEffect(() => {
    if (tab === "invoices") api.get("/invoices").then((r) => setInvoices(r.data));
    if (tab === "revenue") api.get("/revenue/summary").then((r) => setRevenue(r.data));
  }, [tab]);

  const setQty = (code, qty) => {
    setRows((prev) => {
      const found = prev.find((r) => r.product_code === code);
      const next = qty <= 0
        ? prev.filter((r) => r.product_code !== code)
        : found
        ? prev.map((r) => r.product_code === code ? { ...r, quantity: qty } : r)
        : [...prev, { product_code: code, quantity: qty, discount_pct: 0 }];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/site-config/${selectedSite}`, { rows });
      toast.success(`Saved · annual cloud ${fmt(r.data.subscription.cloud_total_pre_tax)} · GST ${fmt(r.data.subscription.gst_amount)}`);
    } catch (e) {
      const errs = e.response?.data?.detail?.validation_errors;
      if (errs && Array.isArray(errs)) errs.forEach((m) => toast.error(m));
      else toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const generateInvoice = async () => {
    if (!selectedSite || selectedSite === "all") { toast.error("Pick a site first"); return; }
    try {
      const r = await api.post(`/invoices/generate/${selectedSite}`);
      toast.success(`Invoice ${r.data.invoice_no} generated — total ${fmt(r.data.grand_total)}`);
      setTab("invoices");
      api.get("/invoices").then((rr) => setInvoices(rr.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
  };

  const downloadInvoice = async (inv) => {
    const res = await fetch(`${API}/invoices/${inv.invoice_id}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${inv.invoice_no}.pdf`; a.click();
  };

  // Group catalog by category
  const grouped = useMemo(() => {
    const g = {};
    for (const p of catalog) (g[p.category] = g[p.category] || []).push(p);
    return g;
  }, [catalog]);

  const rowMap = useMemo(() => Object.fromEntries(rows.map((r) => [r.product_code, r])), [rows]);
  const siteName = sites.find((s) => s.site_id === selectedSite)?.name || "—";

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → Subscription &amp; Revenue</div>
          <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2"><IndianRupee className="w-7 h-7 text-orange-400" strokeWidth={2.5} /> Subscription &amp; Revenue Engine</h1>
          <p className="text-sm text-gray-500 mt-1">Configure equipment per site · auto-priced from master catalog · invoice-ready</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="f360-gradient border-0" data-testid="add-custom-product-btn">
          <PlusCircle className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Custom Product
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-gray-50">
          <TabsTrigger value="configurator" data-testid="tab-cfg"><Boxes className="w-4 h-4 mr-1.5" /> Site Configurator</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-inv"><FileText className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Invoices</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-rev"><TrendingUp className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Revenue Analytics</TabsTrigger>
        </TabsList>

        {/* SITE CONFIGURATOR */}
        <TabsContent value="configurator" className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-4">
          <div className="space-y-4">
            <div className="glass rounded-lg p-3 text-xs text-gray-500">
              Site: <b className="text-orange-300">{siteName}</b> · {rows.length} equipment lines · all unit rates lock to current catalog at save time.
            </div>

            {CAT_ORDER.map((cat) => {
              const items = grouped[cat] || [];
              if (!items.length) return null;
              const catHardwareTotal = items.reduce((s, p) => s + (rowMap[p.code]?.quantity || 0) * p.unit_hardware_rate, 0);
              const catCloudTotal = items.reduce((s, p) => s + (rowMap[p.code]?.quantity || 0) * p.unit_cloud_rate * (1 - (rowMap[p.code]?.discount_pct || 0) / 100), 0);
              return (
                <div key={cat} className="glass rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-5 py-3 flex items-center justify-between">
                    <div className="font-bold uppercase tracking-wider text-sm">{CAT_LABEL[cat] || cat}</div>
                    <div className="text-xs text-gray-500 flex gap-3">
                      <span>Hardware: <b className="text-gray-900">{fmt(catHardwareTotal)}</b></span>
                      <span>Cloud (annual): <b className="text-emerald-300">{fmt(catCloudTotal)}</b></span>
                    </div>
                  </div>
                  <div className="divide-y divide-white/5">
                    {items.map((p) => {
                      const row = rowMap[p.code];
                      const qty = row?.quantity || 0;
                      const isCloud = p.unit_cloud_rate > 0;
                      const eff_hw = row?.override_hardware_rate ?? p.unit_hardware_rate;
                      const eff_cl = row?.override_cloud_rate ?? p.unit_cloud_rate;
                      const lineHw = qty * eff_hw;
                      const lineCl = qty * eff_cl * (1 - (row?.discount_pct || 0) / 100);
                      const hasOverride = (row?.override_hardware_rate ?? null) !== null || (row?.override_cloud_rate ?? null) !== null;
                      const isOpen = !!overrideOpen[p.code];
                      const ed = editing[p.code] || {};
                      return (
                        <div key={p.product_id} className="px-5 py-3" data-testid={`cfg-${p.code}`}>
                          <div className="grid grid-cols-[1fr,auto,auto,auto] items-center gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                                {p.name}
                                {isCloud && <span title="Revenue-generating cloud SaaS line"><Sparkles className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.5} /></span>}
                                {p.is_mandatory_base && <span title="Mandatory base license"><Lock className="w-3.5 h-3.5 text-orange-300" strokeWidth={2.5} /></span>}
                                {p.requires_hardware_id && <span title="Requires the matching hardware"><AlertTriangle className="w-3.5 h-3.5 text-amber-300" strokeWidth={2.5} /></span>}
                                {p.is_custom && <Badge className="bg-purple-500/20 text-purple-200 border-0 text-[9px] px-1.5">CUSTOM</Badge>}
                                {hasOverride && <Badge className="bg-amber-50 text-amber-700 border-0 text-[9px] px-1.5">OVERRIDE</Badge>}
                              </div>
                              <div className="text-[11px] text-gray-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
                                <span>{p.code}</span>
                                <span>· HSN {p.hsn_code || "—"}</span>
                                <span className="flex items-center gap-1">· HW
                                  <input type="number" min="0" value={ed.unit_hardware_rate ?? p.unit_hardware_rate}
                                    onChange={(e) => setEditing((s) => ({ ...s, [p.code]: { ...(s[p.code] || {}), unit_hardware_rate: e.target.value } }))}
                                    onBlur={(e) => { if (Number(e.target.value) !== Number(p.unit_hardware_rate)) saveMasterRate(p.code, "unit_hardware_rate", e.target.value); }}
                                    className="w-20 bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-orange-300 font-mono text-[11px]"
                                    data-testid={`master-hw-${p.code}`} />
                                </span>
                                <span className="flex items-center gap-1">· Cloud
                                  <input type="number" min="0" value={ed.unit_cloud_rate ?? p.unit_cloud_rate}
                                    onChange={(e) => setEditing((s) => ({ ...s, [p.code]: { ...(s[p.code] || {}), unit_cloud_rate: e.target.value } }))}
                                    onBlur={(e) => { if (Number(e.target.value) !== Number(p.unit_cloud_rate)) saveMasterRate(p.code, "unit_cloud_rate", e.target.value); }}
                                    className="w-20 bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-emerald-300 font-mono text-[11px]"
                                    data-testid={`master-cl-${p.code}`} />/yr
                                </span>
                                <button onClick={() => setOverrideOpen((s) => ({ ...s, [p.code]: !isOpen }))} className="text-amber-300 hover:text-amber-200 flex items-center gap-0.5" data-testid={`toggle-override-${p.code}`}>
                                  <Pencil className="w-3 h-3" /> Site override
                                </button>
                                {p.is_custom && (
                                  <button onClick={() => deleteCustomProduct(p.code)} className="text-red-300 hover:text-red-200 flex items-center gap-0.5" data-testid={`delete-${p.code}`}>
                                    <X className="w-3 h-3" strokeWidth={2.5} /> Delete
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" className="w-8 h-8 p-0" onClick={() => setQty(p.code, Math.max(0, qty - 1))} disabled={qty <= 0} data-testid={`dec-${p.code}`}><Minus className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                              <Input type="number" min="0" max={p.max_qty} value={qty} onChange={(e) => setQty(p.code, Math.max(0, Math.min(p.max_qty, Number(e.target.value))))} className="w-16 bg-gray-50 border-gray-200 text-center h-8" data-testid={`qty-${p.code}`} />
                              <Button size="sm" variant="ghost" className="w-8 h-8 p-0" onClick={() => setQty(p.code, qty + 1)} disabled={qty >= p.max_qty} data-testid={`inc-${p.code}`}><Plus className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                            </div>
                            <div className="text-right text-xs min-w-[120px]">
                              {qty > 0 ? (
                                <>
                                  {eff_hw > 0 && <div>HW {fmt(lineHw)}</div>}
                                  {eff_cl > 0 && <div className="text-emerald-300">Cloud {fmt(lineCl)}</div>}
                                </>
                              ) : <span className="text-slate-600">—</span>}
                            </div>
                          </div>
                          {isOpen && (
                            <div className="mt-2 ml-2 pl-3 border-l-2 border-amber-400/30 grid grid-cols-2 gap-3 text-xs" data-testid={`override-panel-${p.code}`}>
                              <label className="flex flex-col gap-1">
                                <span className="text-amber-300/80 text-[10px] uppercase tracking-wider">Override HW rate (this site only)</span>
                                <input type="number" min="0" placeholder={`Default ${p.unit_hardware_rate}`}
                                  value={row?.override_hardware_rate ?? ""}
                                  onChange={(e) => setOverride(p.code, "override_hardware_rate", e.target.value)}
                                  className="bg-gray-50 border border-amber-400/20 rounded px-2 py-1 font-mono"
                                  data-testid={`override-hw-${p.code}`} />
                              </label>
                              <label className="flex flex-col gap-1">
                                <span className="text-amber-300/80 text-[10px] uppercase tracking-wider">Override Cloud rate (this site only)</span>
                                <input type="number" min="0" placeholder={`Default ${p.unit_cloud_rate}`}
                                  value={row?.override_cloud_rate ?? ""}
                                  onChange={(e) => setOverride(p.code, "override_cloud_rate", e.target.value)}
                                  className="bg-gray-50 border border-amber-400/20 rounded px-2 py-1 font-mono"
                                  data-testid={`override-cl-${p.code}`} />
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right rail — billing panel */}
          <div className="space-y-3">
            <div className="glass rounded-lg p-5 sticky top-4" data-testid="billing-panel">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-3"><IndianRupee className="w-3 h-3" strokeWidth={2.5} /> Live Billing</div>
              {preview?.validation_errors?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3 text-xs text-red-200 space-y-1">
                  {preview.validation_errors.map((e, i) => <div key={i} className="flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.5} /> {e}</div>)}
                </div>
              )}
              <Row label="Hardware (one-time)" value={fmt(preview?.hardware_total || 0)} />
              <Row label="Cloud (annual, pre-tax)" value={fmt(preview?.cloud_total_pre_tax || 0)} accent="text-emerald-300" />
              <Row label="GST 18% (cloud only)" value={fmt(preview?.gst_amount || 0)} />
              <div className="border-t border-gray-200 my-3" />
              <Row label="Annual Grand Total" value={fmt(preview?.grand_total_annual || 0)} accent="text-orange-300" big />
              <Row label="Monthly equivalent" value={fmt(preview?.monthly_revenue || 0)} small />

              <Button onClick={save} disabled={busy || !selectedSite || selectedSite === "all" || (preview?.validation_errors?.length || 0) > 0} className="w-full f360-gradient border-0 mt-4" data-testid="save-cfg-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" strokeWidth={2.5} /> : <Save className="w-4 h-4 mr-1" strokeWidth={2.5} />} Save Configuration
              </Button>
              <Button onClick={generateInvoice} variant="outline" className="w-full border-white/15 mt-2" data-testid="gen-inv-btn">
                <FileText className="w-4 h-4 mr-1" strokeWidth={2.5} /> Generate Annual Invoice
              </Button>
            </div>

            {suggestions.length > 0 && (
              <div className="glass rounded-lg p-4 border border-amber-400/20" data-testid="suggest-panel">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2"><Sparkles className="w-3 h-3" strokeWidth={2.5} /> Smart Suggestions</div>
                <div className="space-y-2 text-xs">
                  {suggestions.map((s, i) => (
                    <div key={i} className="bg-amber-400/5 rounded-lg p-2.5 border border-amber-400/10" data-testid={`suggest-${s.kind}-${s.suggest_code}`}>
                      <div className="flex items-start gap-2">
                        {s.kind === "missing_hardware" && <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-300" strokeWidth={2.5} />}
                        {s.kind === "recommend_license" && <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-300" strokeWidth={2.5} />}
                        {s.kind === "mandatory_base" && <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-orange-300" strokeWidth={2.5} />}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{s.suggest_name}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            {s.kind === "missing_hardware" && <>Required for <b>{s.for_name}</b> · qty {s.suggest_qty}</>}
                            {s.kind === "recommend_license" && <>Pairs with <b>{s.for_name}</b> · adds <b className="text-emerald-300">{fmt(s.annual_revenue || 0)}/yr</b></>}
                            {s.kind === "mandatory_base" && <>Mandatory base license — required when any cloud line is enabled</>}
                          </div>
                        </div>
                        <Button size="sm" className="h-6 text-[10px] f360-gradient border-0 px-2" onClick={() => setQty(s.suggest_code, s.suggest_qty)} data-testid={`apply-${s.suggest_code}`}>Add</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* INVOICES */}
        <TabsContent value="invoices" className="space-y-2">
          <div className="glass rounded-lg overflow-hidden">
            <div className="table-scroll">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                  <tr><th className="px-5 py-3 text-left">Invoice No.</th><th className="text-left">Site</th><th className="text-left">Issued</th><th className="text-left">Due</th><th className="text-right">Hardware</th><th className="text-right">Cloud + GST</th><th className="text-right">Total</th><th></th></tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-t border-gray-200" data-testid={`inv-${inv.invoice_id}`}>
                      <td className="px-5 py-2.5 font-mono text-orange-300">{inv.invoice_no}</td>
                      <td>{inv.site_name}</td>
                      <td className="text-xs">{new Date(inv.issued_at).toLocaleDateString("en-IN")}</td>
                      <td className="text-xs">{new Date(inv.due_at).toLocaleDateString("en-IN")}</td>
                      <td className="text-right">{fmt(inv.hardware_subtotal)}</td>
                      <td className="text-right text-emerald-300">{fmt(inv.cloud_subtotal + inv.gst_amount)}</td>
                      <td className="text-right font-bold">{fmt(inv.grand_total)}</td>
                      <td className="text-right pr-4"><Button size="sm" onClick={() => downloadInvoice(inv)} className="bg-gray-100 hover:bg-white/15 h-7" data-testid={`dl-${inv.invoice_id}`}><FileText className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> PDF</Button></td>
                    </tr>
                  ))}
                  {!invoices.length && <tr><td colSpan={8} className="text-center py-8 text-gray-500">No invoices yet. Configure a site and click <b>Generate Annual Invoice</b>.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* REVENUE ANALYTICS */}
        <TabsContent value="revenue" className="space-y-4">
          {revenue && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="ARR" value={fmt(revenue.arr)} icon={TrendingUp} tone="green" />
                <Kpi label="MRR" value={fmt(revenue.mrr)} icon={TrendingUp} tone="orange" />
                <Kpi label="Hardware (one-time)" value={fmt(revenue.hardware_one_time)} icon={Boxes} tone="blue" />
                <Kpi label="Avg ARR / site" value={fmt(revenue.avg_arr_per_site)} icon={Building2} tone="purple" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass rounded-lg p-5">
                  <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-3">Top Cloud Products</div>
                  <div className="space-y-2 text-sm">
                    {revenue.top_products.map((p) => (
                      <div key={p.product_code} className="flex items-center justify-between bg-gray-50 rounded p-2.5">
                        <div><div className="font-semibold">{p.product_name}</div><div className="text-[10px] text-gray-500 font-mono">{p.product_code} · qty {p.qty}</div></div>
                        <div className="font-bold text-emerald-300">{fmt(p.annual_cloud)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="glass rounded-lg p-5">
                  <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-3">Per-Site Annual Revenue</div>
                  <div className="space-y-2 text-sm">
                    {revenue.per_site.map((s) => {
                      const max = revenue.per_site[0]?.annual || 1;
                      const pct = (s.annual / max) * 100;
                      return (
                        <div key={s.site_id}>
                          <div className="flex justify-between text-xs mb-0.5"><span>{sites.find((x) => x.site_id === s.site_id)?.name || s.site_id}</span><span className="font-bold">{fmt(s.annual)}</span></div>
                          <div className="h-2 bg-gray-50 rounded-full overflow-hidden"><div className="h-full f360-gradient" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="glass rounded-lg p-5">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-3">Cloud Revenue by Category</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {revenue.per_category_cloud.map((c) => (
                    <div key={c.category} className="bg-gray-50 rounded p-3"><div className="text-xs text-gray-500 capitalize">{c.category.replace(/_/g, " ")}</div><div className="font-bold text-emerald-300 mt-1">{fmt(c.annual)}</div></div>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <AddProductDialog open={addOpen} onOpenChange={setAddOpen} onSubmit={addCustomProduct} />
    </div>
  );
}

function AddProductDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ code: "", name: "", category: "hardware", sub_category: "", hsn_code: "", unit_hardware_rate: 0, unit_cloud_rate: 0, max_qty: 100, notes: "" });
  const reset = () => setForm({ code: "", name: "", category: "hardware", sub_category: "", hsn_code: "", unit_hardware_rate: 0, unit_cloud_rate: 0, max_qty: 100, notes: "" });
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-lg">
        <DialogHeader><DialogTitle>Add Custom Product to Catalog</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm" data-testid="add-product-form">
          <Field label="Code (auto if blank)"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. CUST-FX01" className="bg-gray-50 border-gray-200" data-testid="add-code" /></Field>
          <Field label="HSN/SAC"><Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-hsn" /></Field>
          <Field label="Name *" full><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-name" /></Field>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="add-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                <SelectItem value="hardware">Hardware</SelectItem>
                <SelectItem value="cloud_license">Cloud License</SelectItem>
                <SelectItem value="lift_monitoring">Lift Monitoring</SelectItem>
                <SelectItem value="accessory">Accessory</SelectItem>
                <SelectItem value="base_license">Base License</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sub-category"><Input value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-subcat" /></Field>
          <Field label="Hardware rate (₹)"><Input type="number" min="0" value={form.unit_hardware_rate} onChange={(e) => setForm({ ...form, unit_hardware_rate: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-hw-rate" /></Field>
          <Field label="Cloud rate (₹/yr)"><Input type="number" min="0" value={form.unit_cloud_rate} onChange={(e) => setForm({ ...form, unit_cloud_rate: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-cl-rate" /></Field>
          <Field label="Max quantity"><Input type="number" min="1" value={form.max_qty} onChange={(e) => setForm({ ...form, max_qty: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-maxqty" /></Field>
          <Field label="Notes" full><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="add-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15">Cancel</Button>
          <Button onClick={() => onSubmit(form)} disabled={!form.name} className="f360-gradient border-0" data-testid="add-product-submit">
            <PlusCircle className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add to Catalog
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, accent = "text-white", big, small }) {
  return (
    <div className={`flex items-center justify-between ${big ? "py-2" : "py-1"}`}>
      <div className={`text-xs text-gray-500 ${small ? "text-[10px]" : ""}`}>{label}</div>
      <div className={`font-bold ${big ? "text-2xl" : small ? "text-xs" : "text-sm"} ${accent}`}>{value}</div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const grad = { green: "from-emerald-400 to-emerald-600", orange: "from-orange-500 to-pink-500", blue: "from-sky-400 to-indigo-500", purple: "from-purple-500 to-indigo-500" };
  return (
    <div className="glass rounded-lg p-5">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad[tone]} flex items-center justify-center mb-3`}><Icon className="w-5 h-5 text-gray-900" /></div>
      <div className="text-2xl font-extrabold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}
